import classNames from 'classnames';
import React, { HTMLAttributes } from 'react';
import { Employee } from '../../pages/People/types';
import { getEmployeeInitialsFromName } from '../../utils/employeeInitials';
import Avatar from '../../../../atomic/molecules/avatar/Avatar';
import ModalSidebar from '../ModalSidebar';
import FlairIcon, { FlairIconName } from '../../../../atomic/atoms/FlairIcon';
import { ExtraFieldsViewArea } from '../ExtraFields/ExtraFieldsViewArea/ExtraFieldsViewArea';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsFormId,
} from '../ExtraFields/types';
import { ExtraFieldsValueToRender } from '../ExtraFields/ExtraFieldsViewArea/ExtraFieldsViewAreaContent';
import { FieldValue } from '../EmployeeData/FieldValue';
import StringToHtml from '../../../../components/StringToHtml';

type Props = {
  show: boolean;
  onClose: () => void;
  employee: Employee | null;
};

export const EmployeeInfoSidebar: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onClose,
  employee,
}) => {
  return (
    <ModalSidebar
      show={show}
      header=""
      onClose={onClose}
      content={<SidebarContent employee={employee} />}
    />
  );
};

type SidebarContentProps = {
  employee: Employee | null;
};

const fieldIconMap: Record<string, FlairIconName> = {
  flair__Department__c: 'business-outline',
  flair__Location__c: 'navigate-circle-outline',
  flair__Manager__c: 'business-outline',
  flair__Company_Email__c: 'mail-outline',
  flair__Company_Phone_Number__c: 'call-outline',
};

const SidebarContent: React.FC<
  React.PropsWithChildren<SidebarContentProps>
> = ({ employee }) => {
  if (!employee) {
    return <div />;
  }

  const renderExtraFieldsItems = (items: ExtraFieldsValueToRender[]) => {
    return (
      <>
        {items.map((item) => {
          const icon = fieldIconMap[item.fieldApiName];
          const isEmail = item.fieldApiName === 'flair__Company_Email__c';
          const displayValue =
            item.inputValue && item.inputValue !== '-' ? item.inputValue : null;

          return (
            <RowContainer key={item.fieldApiName} className="fs-5">
              <LabelContainer>
                {icon && <FlairIcon icon={icon} size="sm" />}
                <Label>{item.label}</Label>
              </LabelContainer>
              <Value>
                {isEmail && displayValue ? (
                  <a href={`mailto:${displayValue}`}>{displayValue}</a>
                ) : item.isRichTextArea && displayValue ? (
                  <StringToHtml text={displayValue} />
                ) : (
                  <FieldValue
                    type={item.fieldType}
                    options={[]}
                    value={displayValue}
                  />
                )}
              </Value>
            </RowContainer>
          );
        })}
      </>
    );
  };

  return (
    <div className="d-flex flex-column p-4">
      <HeaderContainer>
        <Avatar
          avatarUrl={employee.avatarUrl ?? ''}
          initials={getEmployeeInitialsFromName(employee.name)}
          size="xl"
        />
        <NameContainer>
          <NameLabel>{employee.name}</NameLabel>
          <PositionLabel>{employee.position}</PositionLabel>
        </NameContainer>
      </HeaderContainer>

      <ExtraFieldsViewArea
        objectApiName={ExtraFieldsSupportedObject.Employee}
        formId={ExtraFieldsFormId.EmployeeDirectoryDetails}
        recordId={employee.id}
        renderItems={renderExtraFieldsItems}
        hideRecordType={true}
      />
    </div>
  );
};

const HeaderContainer = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    {...rest}
    className={classNames(
      'd-flex',
      'flex-column',
      'align-items-center',
      'justify-content-center',
      'gap-3',
      'mb-6',
      className,
    )}
    ref={ref}
  />
));

const NameContainer = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    {...rest}
    className={classNames(
      'd-flex',
      'flex-column',
      'align-items-center',
      'justify-content-center',
      'gap-2',
      className,
    )}
    ref={ref}
  />
));

const RowContainer = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    {...rest}
    className={classNames(
      'd-flex',
      'justify-content-between',
      'align-items-center',
      'p-3',
      'gap-3',
      'border-bottom',
      className,
    )}
    ref={ref}
  />
));

const LabelContainer = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    {...rest}
    className={classNames(
      'd-flex',
      'flex-nowrap',
      'align-items-center',
      'gap-2',
      'text-dark',
      className,
    )}
    ref={ref}
  />
));

const Label = React.forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(({ className, ...rest }, ref) => (
  <span {...rest} className={classNames('fw-bold', className)} ref={ref} />
));

const Value = React.forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(({ className, ...rest }, ref) => (
  <span {...rest} className={classNames('fw-bold', className)} ref={ref} />
));

const NameLabel = React.forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(({ className, ...rest }, ref) => (
  <span
    {...rest}
    className={classNames('fw-bold', 'fs-2', className)}
    ref={ref}
  />
));

const PositionLabel = React.forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(({ className, ...rest }, ref) => (
  <span
    {...rest}
    className={classNames('fw-bold', 'text-dark', 'fs-5', className)}
    ref={ref}
  />
));
